import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import axios from "axios";
import { motion } from "framer-motion";
import { Crown, Check, Sparkle } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PremiumSuccessPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("polling"); // polling | success | error
  const { checkAuth } = useAuth();
  const navigate = useNavigate();
  const polled = useRef(false);

  useEffect(() => {
    if (!sessionId || polled.current) return;
    polled.current = true;

    let attempts = 0;
    const poll = async () => {
      if (attempts >= 6) {
        setStatus("error");
        return;
      }
      try {
        const res = await axios.get(
          `${API}/payments/checkout/status/${sessionId}`,
          { withCredentials: true }
        );
        if (res.data.payment_status === "paid") {
          setStatus("success");
          await checkAuth(); // refresh user data to pick up is_premium
          return;
        }
        if (res.data.status === "expired") {
          setStatus("error");
          return;
        }
      } catch {}
      attempts++;
      setTimeout(poll, 2000);
    };
    poll();
  }, [sessionId, checkAuth]);

  return (
    <div className="min-h-screen bg-[#FAF7F2] flex items-center justify-center p-6" data-testid="premium-success-page">
      {status === "polling" && (
        <div className="text-center">
          <div className="w-12 h-12 rounded-full bg-[#2D6A4F] animate-breathe mx-auto mb-4" />
          <p className="text-sm text-[#78857F]" data-testid="payment-polling">Confirming your payment...</p>
        </div>
      )}

      {status === "success" && (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center max-w-md"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 200, damping: 15 }}
            className="w-24 h-24 rounded-full bg-[#F4A261]/15 flex items-center justify-center mx-auto mb-6"
          >
            <Crown size={44} weight="duotone" className="text-[#F4A261]" />
          </motion.div>

          <h1 className="font-['Outfit'] text-3xl font-medium text-[#1A2421] mb-2">
            Welcome to Premium
          </h1>
          <p className="text-sm text-[#78857F] mb-6">
            You've unlocked AI reflections, advanced insights, and deeper wellness analytics.
          </p>

          <div className="card-neuro p-6 mb-6 text-left">
            {[
              "AI-powered Coach KB journal reflections",
              "Advanced mood pattern analysis",
              "Journal theme tracking & word insights",
              "Priority sensory reset features",
            ].map((f, i) => (
              <div key={i} className="flex items-center gap-2 py-2">
                <Check size={16} weight="bold" className="text-[#2D6A4F] shrink-0" />
                <span className="text-sm text-[#1A2421]">{f}</span>
              </div>
            ))}
          </div>

          <div className="flex items-center justify-center gap-2 mb-6">
            <Sparkle size={16} weight="duotone" className="text-[#84A98C]" />
            <p className="text-sm text-[#84A98C] italic">"Your wellness journey just leveled up."</p>
          </div>

          <Button
            onClick={() => navigate("/dashboard")}
            data-testid="go-to-dashboard-button"
            className="rounded-full bg-[#2D6A4F] hover:bg-[#1B4936] text-white px-10 h-12 font-medium shadow-md"
          >
            Start Exploring
          </Button>
        </motion.div>
      )}

      {status === "error" && (
        <div className="text-center max-w-md">
          <p className="text-lg text-[#1A2421] font-medium mb-2">Payment couldn't be confirmed</p>
          <p className="text-sm text-[#78857F] mb-4">Please try again or contact support.</p>
          <Button
            onClick={() => navigate("/sessions")}
            variant="outline"
            className="rounded-full border-[#E1D9CD]"
          >
            Go Back
          </Button>
        </div>
      )}
    </div>
  );
}
